import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EntityType } from './EntityReference';

const prefixToEntityType: Record<string, EntityType> = {
  pln: 'plan',
  cnv: 'convention',
  act: 'coAction',
  rpt: 'completionReport',
  pmt: 'postMortem',
  rev: 'codeReview',
  doc: 'document',
  skl: 'skill',
};

const uuidBody = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const entityIdPattern = new RegExp(`^(?:agentteams_)?(${Object.keys(prefixToEntityType).join('|')})_(${uuidBody})$`);

const loadingText = 'Loading...';

export const parsePastedEntityId = (text: string): { entityType: EntityType; entityId: string } | null => {
  const match = text.trim().match(entityIdPattern);
  if (!match) {
    return null;
  }

  const prefix = match[1];
  const rawId = match[2];
  const entityType = prefixToEntityType[prefix];
  if (!entityType || !rawId) {
    return null;
  }

  return { entityType, entityId: rawId };
};

export type PasteEntityIdOptions = {
  fetchEntityTitle: (token: string, projectId: string, entityType: EntityType, entityId: string) => Promise<string>;
  token: string | null;
  projectId: string;
};

export const PasteEntityId = Extension.create<PasteEntityIdOptions>({
  name: 'pasteEntityId',

  addOptions() {
    return {
      fetchEntityTitle: async () => {
        throw new Error('Entity title resolver is required');
      },
      token: null,
      projectId: '',
    };
  },

  addProseMirrorPlugins() {
    const { token, projectId, fetchEntityTitle } = this.options;

    return [
      new Plugin({
        key: new PluginKey('pasteEntityId'),
        props: {
          handlePaste: (view, event) => {
            if (!token || !projectId) return false;

            const text = event.clipboardData?.getData('text/plain')?.trim();
            if (!text) return false;

            const parsed = parsePastedEntityId(text);
            if (!parsed) return false;

            event.preventDefault();

            const { state, dispatch } = view;
            const { from, to } = state.selection;

            // Insert loading placeholder
            const loadingNode = state.schema.text(loadingText);
            const tr = state.tr.replaceWith(from, to, loadingNode);
            dispatch(tr);

            void fetchEntityTitle(token, projectId, parsed.entityType, parsed.entityId)
              .then((title) => {
                const { state: currentState, dispatch: currentDispatch } = view;

                // Find and replace the loading placeholder
                let resolvedFrom = -1;
                let resolvedTo = -1;
                currentState.doc.descendants((node, pos) => {
                  if (resolvedFrom >= 0) return false;
                  if (node.isText && node.text?.includes(loadingText)) {
                    const idx = node.text.indexOf(loadingText);
                    resolvedFrom = pos + idx;
                    resolvedTo = resolvedFrom + loadingText.length;
                    return false;
                  }
                  return true;
                });

                if (resolvedFrom < 0) {
                  // Placeholder was removed by user, do nothing
                  return;
                }

                const entityRefNode = currentState.schema.nodes.entityReference?.create({
                  entityType: parsed.entityType,
                  entityId: parsed.entityId,
                  label: title,
                });

                if (!entityRefNode) return;

                const replaceTr = currentState.tr.replaceWith(resolvedFrom, resolvedTo, entityRefNode);
                currentDispatch(replaceTr);
              })
              .catch(() => {
                const { state: currentState, dispatch: currentDispatch } = view;

                // Replace placeholder with original text on failure
                let resolvedFrom = -1;
                let resolvedTo = -1;
                currentState.doc.descendants((node, pos) => {
                  if (resolvedFrom >= 0) return false;
                  if (node.isText && node.text?.includes(loadingText)) {
                    const idx = node.text.indexOf(loadingText);
                    resolvedFrom = pos + idx;
                    resolvedTo = resolvedFrom + loadingText.length;
                    return false;
                  }
                  return true;
                });

                if (resolvedFrom < 0) return;

                const fallbackNode = currentState.schema.text(text);
                const fallbackTr = currentState.tr.replaceWith(resolvedFrom, resolvedTo, fallbackNode);
                currentDispatch(fallbackTr);
              });

            return true;
          },
        },
      }),
    ];
  },
});
